/**
 * AIImportDialog
 * Multi-step modal that owns the AI Import flow:
 *   Upload → Processing → Review → Decision → Confirm
 *
 * The modal stays open across steps and persists `jobId` so users can come
 * back to a running job from the dashboard's quick-add menu. The deal/no-deal
 * choice is made on the Decision step (after analysis), not up front.
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { useAIImportJob } from '@/hooks/useAIImport';
import { AIImportUploadStep } from './steps/AIImportUploadStep';
import { AIImportProcessingStep } from './steps/AIImportProcessingStep';
import { AIImportReviewStep } from './steps/AIImportReviewStep';
import { AIImportDecisionStep } from './steps/AIImportDecisionStep';
import { AIImportConfirmStep } from './steps/AIImportConfirmStep';
import { nextAIImportStep, type AIImportStep } from './aiImportStep';
import { useAiImportCandidateRatings } from './candidateRatings';
import type { AIImportPlan } from '@/types/ai-import';

export type { AIImportStep };

interface AIImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional jobId to resume — when set, dialog skips the upload step and goes
   * straight to processing or review based on the job's current status.
   */
  resumeJobId?: string;
}

export function AIImportDialog({ isOpen, onClose, resumeJobId }: AIImportDialogProps) {
  const [step, setStep] = useState<AIImportStep>(resumeJobId ? 'processing' : 'upload');
  const [jobId, setJobId] = useState<string | null>(resumeJobId ?? null);
  // The exact plan the user reviewed, handed to the Decision step so it applies
  // those edits rather than re-reading a possibly-stale job.summary from cache.
  const [reviewedPlan, setReviewedPlan] = useState<AIImportPlan | null>(null);
  // Opt-in (default OFF) star-rating analysis, chosen on the upload step and read
  // by processing (candidate-rate) and decision (recompute-on-save). Default OFF
  // per Ryan 2026-07-17: rating must be MANUAL, not fire automatically on import
  // — each candidate preview is a live Claude vision call, and recompute-on-save
  // wrote ratings nobody asked for. The property page's "Re-rate" button is the
  // manual path; leaving the toggle here lets a user still opt in per import.
  const [runStarRating, setRunStarRating] = useState(false);
  const { data: job } = useAIImportJob(jobId ?? undefined);

  // Candidate-rate each plan property once the plan is final, holding the
  // unpersisted previews to surface on the Review/Decision steps.
  const candidateRatings = useAiImportCandidateRatings({
    jobId,
    properties:
      job?.status === 'completed_pending_review' ? job.summary?.properties : undefined,
    enabled: runStarRating,
  });

  // Re-derive step from job status whenever we have a job loaded. Forward-only:
  // never bounces the user off the decision step while the job is still
  // completed_pending_review (see nextAIImportStep).
  useEffect(() => {
    if (!job) return;
    setStep((curr) => nextAIImportStep(curr, job.status));
  }, [job]);

  // Reset on close. Forget a finished job (applied/cancelled/failed) so the next
  // open starts at the upload screen; keep an in-flight or awaiting-review job so
  // reopening resumes it (background-run support).
  useEffect(() => {
    if (isOpen) return;
    const terminal =
      job?.status === 'applied' || job?.status === 'cancelled' || job?.status === 'failed';
    if (!resumeJobId && terminal) {
      setJobId(null);
      setReviewedPlan(null);
      setStep('upload');
    } else {
      setStep(resumeJobId ? 'processing' : jobId ? 'processing' : 'upload');
    }
  }, [isOpen, resumeJobId, job?.status, jobId]);

  const handleClose = () => {
    // Allow background-run: closing on processing leaves the job alive.
    onClose();
  };

  const handleJobCreated = (newJobId: string) => {
    setJobId(newJobId);
    setStep('processing');
  };

  const handleReset = () => {
    setJobId(null);
    setReviewedPlan(null);
    setRunStarRating(false);
    setStep('upload');
  };

  const titleByStep: Record<AIImportStep, string> = {
    upload: 'AI Import — Add Files',
    processing: 'AI Import — Analyzing',
    review: 'AI Import — Review',
    decision: 'AI Import — Create a deal?',
    confirm: 'AI Import — Complete',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleByStep[step]}
      description={
        step === 'upload'
          ? 'Upload OMs, T-12s, rent rolls, or broker emails. Claude will classify, extract, and propose what it finds.'
          : step === 'decision'
            ? 'Review complete — choose what to do with what Claude found.'
            : undefined
      }
      size={step === 'review' ? '5xl' : '3xl'}
      // Only the final success screen closes on overlay click — otherwise an
      // accidental click (or focus returning from the MS sign-in popup) could
      // drop staged files / an in-progress review.
      closeOnOverlayClick={step === 'confirm'}
      maxHeight="max-h-[92vh]"
    >
      <div className="overflow-y-auto flex-1 min-h-0">
        {step === 'upload' && (
          <AIImportUploadStep
            onJobCreated={handleJobCreated}
            onCancel={handleClose}
            runStarRating={runStarRating}
            onRunStarRatingChange={setRunStarRating}
          />
        )}
        {step === 'processing' && jobId && (
          <AIImportProcessingStep
            jobId={jobId}
            onReady={() => setStep('review')}
            onClose={handleClose}
          />
        )}
        {step === 'review' && jobId && (
          <AIImportReviewStep
            jobId={jobId}
            candidateRatings={candidateRatings}
            onContinue={(plan) => {
              setReviewedPlan(plan);
              setStep('decision');
            }}
            onClose={handleClose}
          />
        )}
        {step === 'decision' && jobId && reviewedPlan && (
          <AIImportDecisionStep
            jobId={jobId}
            plan={reviewedPlan}
            candidateRatings={candidateRatings}
            runStarRating={runStarRating}
            onApplied={() => setStep('confirm')}
            onBack={() => setStep('review')}
            onClose={handleClose}
          />
        )}
        {step === 'confirm' && jobId && (
          <AIImportConfirmStep
            jobId={jobId}
            onClose={handleClose}
            onStartAnother={handleReset}
          />
        )}
      </div>
    </Modal>
  );
}
